import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../utils/errorUtils";
import * as actions from "../slices/invoice";
import { mapInvoiceToSummary } from "../../mappers/invoiceMapper";

export const getInvoices =
  ({ search = "", startDate, endDate, page = 1, size = 10 }) =>
  async (dispatch) => {
    dispatch(actions.setLoadingInvoices());
    try {
      const params = { page, size, startDate, endDate };
      if (search) params.search = search;
      const { data } = await axiosInstance.get("/invoices", {
        params,
        headers: { requiresAuth: true },
      });
      if (data.status === "SUCCESS") {
        dispatch(
          actions.setInvoices({
            content: data.data.content,
            pagination: data.data.pagination,
          }),
        );
      }
    } catch (error) {
      dispatch(actions.setErrorLoadingInvoices(getErrorMessage(error)));
    }
  };

export const getInvoiceById = (id) => async (dispatch) => {
  dispatch(actions.setLoadingInvoice());
  try {
    const { data } = await axiosInstance.get(`/invoices/${id}`, {
      headers: { requiresAuth: true },
    });
    if (data.status === "SUCCESS")
      dispatch(actions.setCurrentInvoice(data.data));
  } catch (error) {
    dispatch(actions.setErrorLoadingInvoice(getErrorMessage(error)));
  }
};

export const generateRenewalInvoice = (payload) => async (dispatch) => {
  dispatch(actions.setLoadingGenerateRenewal());
  try {
    const { data } = await axiosInstance.post(
      "/invoices/generate-renewal",
      payload,
      { headers: { requiresAuth: true } },
    );
    if (data.status === "SUCCESS") {
      toast.success(data.message || "Renewal invoice generated!");
      const summary = mapInvoiceToSummary(data.data);
      dispatch(actions.generateRenewalSuccess(summary));
      dispatch(
        actions.setServerResponseStatus({
          operation: "generate",
          status: data.statusCode,
        }),
      );
    }
  } catch (error) {
    const msg = getErrorMessage(error);
    toast.error(msg);
    dispatch(actions.setErrorGeneratingRenewal(msg));
  }
};

export const recordInvoicePayment = (id, payload) => async (dispatch) => {
  dispatch(actions.setLoadingPayInvoice());
  try {
    const { data } = await axiosInstance.post(
      `/invoices/payments/${id}`,
      payload,
      { headers: { requiresAuth: true } },
    );
    if (data.status === "SUCCESS") {
      toast.success(data.message || "Payment recorded!");
      dispatch(actions.updateInvoiceSuccess(data.data));
      dispatch(
        actions.setServerResponseStatus({
          operation: "pay",
          status: data.statusCode,
        }),
      );
    }
  } catch (error) {
    const msg = getErrorMessage(error);
    toast.error(msg);
    dispatch(actions.setErrorPayingInvoice(msg));
  }
};

export const allocateBulkPayment = (payload) => async (dispatch) => {
  dispatch(actions.setLoadingBulkAllocate());
  try {
    const { data } = await axiosInstance.post(
      "/invoices/payments/bulk-allocate",
      payload,
      { headers: { requiresAuth: true } },
    );
    if (data.status === "SUCCESS") {
      toast.success(data.message || "Bulk allocation completed!");
      dispatch(
        actions.setServerResponseStatus({
          operation: "bulk-allocate",
          status: data.statusCode,
        }),
      );
    }
  } catch (error) {
    const msg = getErrorMessage(error);
    toast.error(msg);
    dispatch(actions.setErrorBulkAllocate(msg));
  }
};

export const getInvoicePdf = (id) => async (dispatch) => {
  dispatch(actions.setLoadingPdf());
  try {
    const { data } = await axiosInstance.get(`/invoices/pdf/${id}`, {
      headers: { requiresAuth: true },
      responseType: "blob",
    });
    const pdfBlobUrl = window.URL.createObjectURL(
      new Blob([data], { type: "application/pdf" }),
    );
    dispatch(actions.setPdfUrl(pdfBlobUrl));
  } catch (error) {
    const msg = getErrorMessage(error);
    toast.error("Failed to load PDF preview: " + msg);
    dispatch(actions.setErrorLoadingPdf(msg));
  }
};

export const emailInvoice = (id, emailPayload) => async (dispatch) => {
  dispatch(actions.setLoadingEmail());
  try {
    const { data } = await axiosInstance.post(
      `/invoices/email/${id}`,
      emailPayload,
      { headers: { requiresAuth: true } },
    );
    if (data.status === "SUCCESS") {
      toast.success(data.message || "Invoice emailed successfully!");
      dispatch(actions.setEmailSuccess());
      dispatch(
        actions.setServerResponseStatus({
          operation: "email",
          status: data.statusCode,
        }),
      );
      return true;
    }
  } catch (error) {
    const msg = getErrorMessage(error);
    toast.error(msg);
    dispatch(actions.setErrorEmailing(msg));
    return false;
  }
};

export const resetInvoiceServerStatus = () => (dispatch) => {
  dispatch(actions.setServerResponseStatus(null));
};
